// ──────────────────────────────────────────────
// Init-Segment Extractor
//
// MediaRecorder with `timeslice` produces chunks where only chunk 0
// contains the container init segment (WebM EBML header or MP4 ftyp+moov).
// Subsequent chunks are raw media data and cannot be decoded standalone.
//
// This module extracts the init segment from chunk 0 and provides a
// prepend function so every chunk becomes independently decodable.
// See ADR-006 for design rationale.
// ──────────────────────────────────────────────

/**
 * WebM Cluster element ID (4 bytes).
 * In EBML encoding, this is the first Cluster in the Segment.
 * Everything before it is the init segment (EBML header + Segment/Tracks).
 */
const WEBM_CLUSTER_ID = new Uint8Array([0x1f, 0x43, 0xb6, 0x75]);

/**
 * Extract the init segment from a WebM first chunk.
 *
 * Scans for the Cluster element ID `0x1F43B675`. Everything before
 * that offset is the init segment (EBML header + Segment info + Tracks).
 *
 * EBML element IDs use specific leading-bit patterns that prevent
 * false positives inside codec private data or audio frames.
 */
export function extractWebmInitSegment(firstChunk: ArrayBuffer): ArrayBuffer {
  const view = new Uint8Array(firstChunk);
  const len = view.length;

  if (len < 4) {
    throw new Error('WebM chunk too small to contain init segment');
  }

  for (let i = 0; i <= len - 4; i++) {
    if (
      view[i] === WEBM_CLUSTER_ID[0] &&
      view[i + 1] === WEBM_CLUSTER_ID[1] &&
      view[i + 2] === WEBM_CLUSTER_ID[2] &&
      view[i + 3] === WEBM_CLUSTER_ID[3]
    ) {
      if (i === 0) {
        throw new Error('WebM chunk starts with Cluster — no init segment found');
      }
      return firstChunk.slice(0, i);
    }
  }

  throw new Error('No Cluster element found in WebM chunk');
}

/**
 * Extract the init segment from a fragmented MP4 first chunk.
 *
 * Uses mp4box.js to parse the MP4 container. The init segment consists
 * of the ftyp + moov atoms. This function loads mp4box dynamically to
 * avoid bundling it for browsers that use WebM (Chrome, Firefox).
 *
 * Required for Safari <18.4 which produces `audio/mp4` output.
 */
export async function extractMp4InitSegment(firstChunk: ArrayBuffer): Promise<ArrayBuffer> {
  // Dynamic import — only loaded when MP4 path is needed
  const mp4box = await import('mp4box');
  const file = mp4box.createFile();

  return new Promise<ArrayBuffer>((resolve, reject) => {
    let resolved = false;

    file.onReady = (info) => {
      try {
        // Find the first audio track
        const audioTrack = info.tracks?.find(
          (t: { type?: string }) => t.type === 'audio',
        );

        if (!audioTrack) {
          reject(new Error('No audio track found in MP4 chunk'));
          return;
        }

        // Set up segmentation to get the init segment
        file.setSegmentOptions(audioTrack.id, null as unknown, {});
        const result = file.initializeSegmentation();

        if (!result?.buffer) {
          reject(new Error('mp4box.js failed to produce init segment'));
          return;
        }

        // The buffer is an MP4BoxBuffer (extends ArrayBuffer)
        // Copy to a plain ArrayBuffer
        const initSegment = new ArrayBuffer(result.buffer.byteLength);
        new Uint8Array(initSegment).set(new Uint8Array(result.buffer));

        resolved = true;
        resolve(initSegment);
      } catch (err) {
        reject(
          new Error(
            `Failed to extract MP4 init segment: ${err instanceof Error ? err.message : String(err)}`,
          ),
        );
      }
    };

    file.onError = (_module: string, message: string) => {
      if (!resolved) {
        reject(new Error(`mp4box.js parse error: ${message}`));
      }
    };

    // mp4box.js requires an ArrayBuffer with a `fileStart` property
    const buffer = firstChunk.slice(0) as ArrayBuffer & { fileStart: number };
    buffer.fileStart = 0;
    file.appendBuffer(buffer);
  });
}

/**
 * Extract the init segment from the first chunk of a recording.
 *
 * Dispatches to the WebM or MP4 extraction strategy based on MIME type.
 *
 * @param firstChunk - The first chunk's ArrayBuffer data (chunk 0)
 * @param mimeType - The negotiated MIME type (e.g. 'audio/webm;codecs=opus' or 'audio/mp4')
 * @returns The init segment bytes
 */
export async function extractInitSegment(
  firstChunk: ArrayBuffer,
  mimeType: string,
): Promise<ArrayBuffer> {
  if (mimeType.startsWith('audio/mp4') || mimeType.startsWith('video/mp4')) {
    return extractMp4InitSegment(firstChunk);
  }

  // WebM and all other formats use the EBML-based extraction
  return extractWebmInitSegment(firstChunk);
}

/**
 * Prepend an init segment to a chunk, producing an independently decodable file.
 *
 * @param initSegment - The init segment bytes (from extractInitSegment)
 * @param chunk - The raw chunk data (without init segment)
 * @returns A new ArrayBuffer containing initSegment + chunk
 */
export function prependInitSegment(
  initSegment: ArrayBuffer,
  chunk: ArrayBuffer,
): ArrayBuffer {
  const result = new Uint8Array(initSegment.byteLength + chunk.byteLength);
  result.set(new Uint8Array(initSegment), 0);
  result.set(new Uint8Array(chunk), initSegment.byteLength);
  return result.buffer;
}
